import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AppLayout } from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { logAudit } from '@/lib/audit';
import TaskModal, { TaskFormData } from '../components/TaskModal';
import TaskComments from '@/pages/app/projeto/[id]/components/TaskComments';
import TaskViewModal from '@/pages/app/projeto/[id]/components/TaskViewModal';

interface Task {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  type: 'feature' | 'bug';
  priority: string;
  status: string;
  board_status: string;
  requester_name: string | null;
  observations: string | null;
  estimate_hours: number;
  estimate_days: number;
  value: number;
  paid: boolean;
  sprint_desired: boolean;
  sprint_id: string | null;
  position: number;
  created_by: string | null;
  created_at: string;
}

interface Sprint {
  id: string;
  name: string;
  status: string;
}

interface Project {
  id: string;
  name: string;
  _client_name?: string;
}

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  low: { label: 'Baixa', color: 'text-slate-400', icon: 'ri-arrow-down-line' },
  medium: { label: 'Média', color: 'text-orange-400', icon: 'ri-arrow-right-line' },
  high: { label: 'Alta', color: 'text-red-500', icon: 'ri-arrow-up-line' },
  critical: { label: 'Crítica', color: 'text-red-700', icon: 'ri-arrow-up-double-line' },
};

const PRIORITY_ORDER: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };

export default function BacklogPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuthContext();
  const isAdmin = profile?.role === 'admin';

  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterSprint, setFilterSprint] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'priority' | 'created_at' | 'position'>('priority');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [commentsTask, setCommentsTask] = useState<Task | null>(null);
  const [viewTask, setViewTask] = useState<Task | null>(null);

  const locationState = location.state as { openTaskId?: string } | null;
  const [pendingOpenTaskId] = useState<string | null>(locationState?.openTaskId || null);

  const loadData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);

    const [projRes, tasksRes, sprintsRes] = await Promise.all([
      supabase.from('projects').select('id, name, client_id').eq('id', projectId).maybeSingle(),
      supabase.from('tasks').select('*').eq('project_id', projectId).order('position').order('created_at', { ascending: false }),
      supabase.from('sprints').select('*').eq('project_id', projectId).order('created_at'),
    ]);

    if (projRes.data) {
      const { data: client } = projRes.data.client_id
        ? await supabase.from('clients').select('name').eq('id', projRes.data.client_id).maybeSingle()
        : { data: null };
      setProject({ ...projRes.data, _client_name: client?.name || '—' });
    }

    setTasks(tasksRes.data || []);
    setSprints(sprintsRes.data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-open TaskViewModal when coming from a notification
  useEffect(() => {
    if (!pendingOpenTaskId || tasks.length === 0) return;
    const target = tasks.find(t => t.id === pendingOpenTaskId);
    if (target) {
      setViewTask(target);
    }
  }, [pendingOpenTaskId, tasks]);

  const backlogTasks = tasks.filter((t) => t.board_status === 'backlog');

  const filtered = backlogTasks.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch = !q || t.title.toLowerCase().includes(q) || (t.requester_name || '').toLowerCase().includes(q);
    const matchType = !filterType || t.type === filterType;
    const matchPriority = !filterPriority || t.priority === filterPriority;
    const matchSprint = !filterSprint
      ? true
      : filterSprint === 'desired'
      ? t.sprint_desired
      : t.sprint_id === filterSprint;
    return matchSearch && matchType && matchPriority && matchSprint;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'priority') return (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99);
    if (sortBy === 'created_at') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    return a.position - b.position;
  });

  const handleSave = async (data: TaskFormData) => {
    if (!projectId) return;
    if (editingTask) {
      const { error } = await supabase.from('tasks').update({
        ...data,
        updated_at: new Date().toISOString(),
      }).eq('id', editingTask.id);
      if (!error) { await logAudit('update', 'tasks', editingTask.id, { title: data.title }); loadData(); }
    } else {
      const maxPos = backlogTasks.length > 0 ? Math.max(...backlogTasks.map((t) => t.position)) + 1 : 0;
      const { data: inserted, error } = await supabase.from('tasks').insert({
        ...data,
        project_id: projectId,
        board_status: 'backlog',
        status: 'backlog',
        position: maxPos,
        created_by: profile?.id,
      }).select().maybeSingle();
      if (!error && inserted) { await logAudit('insert', 'tasks', inserted.id, { title: data.title }); loadData(); }
    }
    setModalOpen(false);
    setEditingTask(null);
  };

  const handleDelete = async (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    if (!isAdmin && task.board_status !== 'backlog') return;
    const { error } = await supabase.from('tasks').delete().eq('id', taskId);
    if (!error) { await logAudit('delete', 'tasks', taskId, {}); loadData(); }
    setDeleteConfirm(null);
  };

  const handleMoveToTodo = async (task: Task) => {
    const { error } = await supabase.from('tasks').update({
      board_status: 'todo',
      status: 'todo',
      updated_at: new Date().toISOString(),
    }).eq('id', task.id);
    if (!error) { await logAudit('update', 'tasks', task.id, { board_status: 'todo' }); loadData(); }
  };

  const openEdit = (t: Task) => { setEditingTask(t); setModalOpen(true); };
  const openNew = () => { setEditingTask(null); setModalOpen(true); };

  const fmt = (d: string) => new Date(d).toLocaleDateString('pt-BR');

  const sprintName = (id: string | null) => {
    if (!id) return null;
    return sprints.find((s) => s.id === id)?.name || null;
  };

  return (
    <AppLayout>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/scrum')}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base"></i>
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-slate-800 text-xl font-bold">{project?.name || '...'}</h1>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">Backlog</span>
            </div>
            <p className="text-slate-400 text-xs mt-0.5">{project?._client_name} · {backlogTasks.length} iten{backlogTasks.length !== 1 ? 's' : ''} no backlog</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/admin/scrum/${projectId}/pricing`)}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-money-dollar-circle-line text-sm"></i>
            Precificação
          </button>
          <button
            onClick={() => navigate(`/admin/scrum/${projectId}/board`)}
            className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-kanban-view text-sm"></i>
            Ver Board
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-1.5 px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Novo Item
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por título ou solicitante..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300"
          />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="">Todos os tipos</option>
          <option value="feature">Nova Demanda</option>
          <option value="bug">Bug/Correção</option>
        </select>
        <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="">Todas as prioridades</option>
          <option value="critical">Crítica</option>
          <option value="high">Alta</option>
          <option value="medium">Média</option>
          <option value="low">Baixa</option>
        </select>
        <select value={filterSprint} onChange={(e) => setFilterSprint(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="">Todas as sprints</option>
          <option value="desired">Desejados para próxima sprint</option>
          {sprints.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'priority' | 'created_at' | 'position')} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer">
          <option value="priority">Ordenar por prioridade</option>
          <option value="created_at">Ordenar por data</option>
          <option value="position">Ordenar por posição</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <i className="ri-list-check-2 text-5xl mb-4"></i>
          <p className="text-sm font-medium text-slate-500">Backlog vazio</p>
          <p className="text-xs mt-1">Adicione itens para começar o planejamento</p>
          <button onClick={openNew} className="mt-4 text-cyan-500 text-sm hover:underline cursor-pointer">Criar primeiro item</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-slate-500 font-medium w-8"></th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Item</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Tipo</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Prioridade</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Solicitante</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Estimativa</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Valor</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Sprint</th>
                  <th className="text-left px-4 py-3 text-slate-500 font-medium">Criado em</th>
                  <th className="text-right px-4 py-3 text-slate-500 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((task) => {
                  const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                  return (
                    <tr key={task.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setViewTask(task)}
                            title="Visualizar item"
                            className="w-6 h-6 flex items-center justify-center cursor-pointer text-slate-300 hover:text-cyan-500 transition-colors"
                          >
                            <i className="ri-eye-line text-sm"></i>
                          </button>
                          {task.sprint_desired && (
                            <i className="ri-star-fill text-amber-400 text-sm" title="Desejado para próxima sprint"></i>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <div className="min-w-0">
                          <p className="text-slate-800 font-medium truncate overflow-hidden whitespace-nowrap">{task.title}</p>
                          {task.description && <p className="text-slate-400 text-xs truncate mt-0.5">{task.description}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${task.type === 'bug' ? 'bg-red-50 text-red-600' : 'bg-violet-50 text-violet-600'}`}>
                          <i className={task.type === 'bug' ? 'ri-bug-line' : 'ri-add-circle-line'}></i>
                          {task.type === 'bug' ? 'Bug' : 'Demanda'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${prio.color}`}>
                          <i className={prio.icon}></i>
                          {prio.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{task.requester_name || '—'}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        {task.estimate_hours > 0 ? `${task.estimate_hours}h` : ''}
                        {task.estimate_hours > 0 && task.estimate_days > 0 ? ' / ' : ''}
                        {task.estimate_days > 0 ? `${task.estimate_days}d` : ''}
                        {!task.estimate_hours && !task.estimate_days ? '—' : ''}
                      </td>
                      <td className="px-4 py-3">
                        {task.value > 0 ? (
                          <div className="flex items-center gap-1">
                            <span className="text-slate-700 text-xs font-medium">
                              {task.value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                            {task.paid && <i className="ri-checkbox-circle-fill text-emerald-500 text-xs" title="Pago"></i>}
                          </div>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {task.sprint_id ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-cyan-50 text-cyan-700">{sprintName(task.sprint_id)}</span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">{fmt(task.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {isAdmin && (
                            <button
                              onClick={() => handleMoveToTodo(task)}
                              title="Mover para A Fazer"
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-arrow-right-circle-line text-sm"></i>
                            </button>
                          )}
                          <button
                            onClick={() => setCommentsTask(task)}
                            title="Comentários"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-chat-3-line text-sm"></i>
                          </button>
                          <button
                            onClick={() => openEdit(task)}
                            title="Editar"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
                          >
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          {(isAdmin || (task.created_by === profile?.id && task.board_status === 'backlog')) && (
                            <button
                              onClick={() => setDeleteConfirm(task.id)}
                              title="Excluir"
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="text-slate-800 font-semibold mb-2">Excluir item?</h3>
            <p className="text-slate-500 text-sm mb-5">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <TaskModal
          task={editingTask ? {
            id: editingTask.id,
            title: editingTask.title,
            description: editingTask.description || '',
            type: editingTask.type,
            priority: editingTask.priority,
            requester_name: editingTask.requester_name || '',
            observations: editingTask.observations || '',
            estimate_hours: editingTask.estimate_hours,
            estimate_days: editingTask.estimate_days,
            value: editingTask.value,
            paid: editingTask.paid,
            sprint_desired: editingTask.sprint_desired,
            board_status: editingTask.board_status,
            sprint_id: editingTask.sprint_id,
          } : undefined}
          sprints={sprints}
          isAdmin={isAdmin}
          onClose={() => { setModalOpen(false); setEditingTask(null); }}
          onSave={handleSave}
        />
      )}

      {/* Visualizar item com chat integrado */}
      {viewTask && (
        <TaskViewModal
          task={{
            id: viewTask.id,
            project_id: viewTask.project_id,
            title: viewTask.title,
            description: viewTask.description,
            type: viewTask.type,
            priority: viewTask.priority,
            board_status: viewTask.board_status,
            requester_name: viewTask.requester_name,
            observations: viewTask.observations,
            estimate_hours: viewTask.estimate_hours,
            estimate_days: viewTask.estimate_days,
            value: viewTask.value,
            paid: viewTask.paid,
            sprint_desired: viewTask.sprint_desired,
            sprint_id: viewTask.sprint_id,
            position: viewTask.position,
            client_priority: 0,
            created_by: viewTask.created_by,
            created_at: viewTask.created_at,
          }}
          userId={profile?.id || ''}
          userName={profile?.full_name || 'Admin'}
          canEdit={isAdmin}
          onEdit={() => { setViewTask(null); openEdit(viewTask); }}
          onClose={() => setViewTask(null)}
        />
      )}

      {/* Comentários */}
      {commentsTask && (
        <TaskComments
          task={{
            id: commentsTask.id,
            project_id: commentsTask.project_id,
            title: commentsTask.title,
            description: commentsTask.description,
            type: commentsTask.type,
            priority: commentsTask.priority,
            board_status: commentsTask.board_status,
            requester_name: commentsTask.requester_name,
            observations: commentsTask.observations,
            estimate_hours: commentsTask.estimate_hours,
            estimate_days: commentsTask.estimate_days,
            value: commentsTask.value,
            paid: commentsTask.paid,
            sprint_desired: commentsTask.sprint_desired,
            sprint_id: commentsTask.sprint_id,
            position: commentsTask.position,
            client_priority: 0,
            created_by: commentsTask.created_by,
            created_at: commentsTask.created_at,
          }}
          userId={profile?.id || ''}
          userName={profile?.full_name || 'Admin'}
          onClose={() => setCommentsTask(null)}
        />
      )}
    </AppLayout>
  );
}
